import clsx from 'clsx';
import { RefObject } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import HabitatLiveStreamPlayer from '@/components/HabitatLiveStreamPlayer';
import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import { useLiveStreamPlayerStateContext } from '@/components/LiveStreamPlayer/contexts';
import LiveStreamPlayerLoadingContent from '@/components/LiveStreamPlayer/LiveStreamPlayerLoadingContent';
import useFullscreenScale from '@/hooks/useFullscreenScale';

import AmbientOnboardingModal from '../AmbientOnboardingModal';
import HomeLiveStreamPlayerDesktopControls from './HomeLiveStreamPlayerDesktopControls';
import HomeLiveStreamPlayerMobileControls from './HomeLiveStreamPlayerMobileControls';

interface HomeLiveStreamPlayerOverlayProps {
  miniMode?: boolean;
  preferLiveStreamMutedRef: RefObject<boolean>;
  containerRef: RefObject<HTMLDivElement>;
  isAmbientMode?: boolean;
  onAmbientModeChange?: (isAmbientMode: boolean) => void;
}

const HomeLiveStreamPlayerOverlay = ({
  containerRef,
  miniMode,
  preferLiveStreamMutedRef,
  isAmbientMode,
  onAmbientModeChange,
}: HomeLiveStreamPlayerOverlayProps) => {
  const isMobileLayout = useIsMobileLayout();
  const { scaleClassName: fullscreenScaleClassName } = useFullscreenScale(containerRef);

  const {
    isPlaying,
    isVideoLoaded,
    volume: liveStreamVolume,
    isMuted: isLiveStreamMuted,
    requestChangePlayingState,
    requestChangeMuteState,
    requestChangeVolume,
  } = useLiveStreamPlayerStateContext();

  const [isMuted, setIsMuted] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(liveStreamVolume);
  const volumeRef = useRef(currentVolume);

  // Tracks whether the user explicitly paused the stream in ambient mode.
  // Survives habitat rotation because this component instance is preserved (no key changes in ancestors).
  const userPausedInAmbientRef = useRef(false);

  const onMutedChange = (newValue: boolean) => {
    if (!isAmbientMode) {
      requestChangeMuteState(newValue);
    }
    // eslint-disable-next-line no-param-reassign
    preferLiveStreamMutedRef.current = newValue;
    setIsMuted(newValue);
  };

  const onVolumeChange = (newValue: number) => {
    volumeRef.current = newValue;
    if (!isAmbientMode) {
      requestChangeVolume(newValue);
    }
    setCurrentVolume(newValue);
    const isMuted = newValue === 0;
    // eslint-disable-next-line no-param-reassign
    preferLiveStreamMutedRef.current = isMuted;
    setIsMuted(isMuted);
  };

  useEffect(() => {
    if (!isVideoLoaded) return;
    if (miniMode) {
      requestChangeMuteState(true);
    } else {
      requestChangeMuteState(!!preferLiveStreamMutedRef.current);
    }
  }, [miniMode, requestChangeMuteState, preferLiveStreamMutedRef, isVideoLoaded]);

  useEffect(() => {
    if (miniMode || !isVideoLoaded) {
      return;
    }
    if (isAmbientMode) {
      requestChangeMuteState(true);
      // unmute background music when entering ambient mode (live stream audio is muted above)
      setIsMuted(false);
    } else if (!preferLiveStreamMutedRef.current) {
      requestChangeVolume(volumeRef.current);
    }
  }, [isAmbientMode, requestChangeMuteState, requestChangeVolume, preferLiveStreamMutedRef, isVideoLoaded, miniMode]);

  // Called when the user explicitly clicks the play/pause button in the controls.
  // Only tracks intent in ambient mode to preserve it across habitat rotations.
  const onPlayingChange = (newIsPlaying: boolean) => {
    if (isAmbientMode) {
      userPausedInAmbientRef.current = !newIsPlaying;
    }
  };

  // After habitat rotation, the new iframe auto-plays. If the user had explicitly paused,
  // re-pause the new stream immediately. Reset the paused flag when ambient mode is turned off
  // so the next ambient session starts fresh.
  useEffect(() => {
    if (isAmbientMode && isVideoLoaded && isPlaying && userPausedInAmbientRef.current) {
      requestChangePlayingState(false);
    }
    if (!isAmbientMode) {
      userPausedInAmbientRef.current = false;
    }
  }, [isAmbientMode, isVideoLoaded, isPlaying, requestChangePlayingState]);

  if (!isVideoLoaded && miniMode) {
    return <p className="preflight preflight-p">Loading...</p>;
  }

  // Also show pause content while the re-pause effect is pending after habitat rotation,
  // so the controls and onboarding modal don't briefly flash.
  const shouldForceShowPaused = isAmbientMode && isVideoLoaded && userPausedInAmbientRef.current;

  if ((!isPlaying || shouldForceShowPaused) && isVideoLoaded) {
    return (
      <HabitatLiveStreamPlayer.FallbackContent>
        {miniMode ? (
          <p className="preflight preflight-p">Paused</p>
        ) : (
          <HabitatLiveStreamPlayer.PauseContent
            onClick={() => {
              userPausedInAmbientRef.current = false;
              requestChangePlayingState(true);
            }}
          />
        )}
      </HabitatLiveStreamPlayer.FallbackContent>
    );
  }

  return (
    <>
      {!isVideoLoaded && <LiveStreamPlayerLoadingContent />}

      {/* Live tag */}
      {isPlaying && (
        <div
          className={clsx(
            'absolute flex items-center gap-1 rounded bg-olive-4 px-1.5 py-1',
            miniMode && 'right-2 top-2',
            !miniMode && (fullscreenScaleClassName ? 'right-14 top-10' : 'right-5 top-3 desktop:px-2'),
            fullscreenScaleClassName && `origin-top-right ${fullscreenScaleClassName}`,
          )}
        >
          <span className="size-1 rounded-full bg-system-red desktop:size-1.5" />
          <span className={clsx('font-bold leading-none', miniMode ? 'text-[8px]' : 'text-[10px] desktop:text-xs')}>
            LIVE
          </span>
        </div>
      )}

      {/* Controls */}
      {isMobileLayout ? (
        isPlaying && <HomeLiveStreamPlayerMobileControls containerRef={containerRef} onMutedChange={onMutedChange} />
      ) : (
        <HomeLiveStreamPlayerDesktopControls
          containerRef={containerRef}
          miniMode={miniMode}
          isAmbientMode={isAmbientMode}
          isMuted={isAmbientMode ? isMuted : isLiveStreamMuted}
          onMutedChange={onMutedChange}
          onPlayingChange={onPlayingChange}
          volume={isAmbientMode ? currentVolume : liveStreamVolume}
          onVolumeChange={onVolumeChange}
          onAmbientModeChange={onAmbientModeChange}
        />
      )}

      {isAmbientMode && <AmbientOnboardingModal />}
    </>
  );
};

export default HomeLiveStreamPlayerOverlay;
